import buttonShapes from "@insite/mobius/Button/buttonShapes";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import buttonTypes from "@insite/mobius/Button/buttonTypes";
import hoverAnimations from "@insite/mobius/Button/hoverAnimations";
// import { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button/Props"; // Starter Custom Code
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import get from "@insite/mobius/utilities/get";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { applyStyleModifiers } from "styled-components-modifiers";

/* Starter Custom Code - Start */
import LoadingSpinner, { LoadingSpinnerProps, LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import HistoryContext from "@insite/mobius/utilities/HistoryContext";
import isRelativeUrl from "@insite/mobius/utilities/isRelativeUrl";
import { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@starter/MobiusOverrides/Button/Props";
import useExistingRef from "@toolkit/Framework/Common/Hooks/useExistingRef";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
/* Starter Custom Code - End */

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
        // this is needed because styled-components is confused
        disabled: boolean;
    };

// this should be BWT but typescript is not happy with "Type instantiation is excessively deep and possibly infinite." and nothing else was working
const ButtonWrapper = styled.button<any>`
    cursor: pointer;
    font-family: inherit;
    min-height: ${({ _sizeVariant, _size }) => _size || get(buttonSizeVariants, [_sizeVariant, "height"])}px;
    line-height: 1;
    backface-visibility: hidden;
    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
    word-break: keep-all;
    ${({ shadow, theme }) =>
        shadow &&
        `
        box-shadow: ${get(theme, "shadows.1")};
    `}
    padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};
    ${applyStyleModifiers(buttonShapes, "_shape")}
    ${applyStyleModifiers(buttonTypes, "buttonType")}
    ${applyStyleModifiers(hoverAnimations, "hoverAnimation")}
    &:disabled {
        cursor: not-allowed;
    }
    ${ButtonIcon} {
        position: relative;
        top: 0.125em;
    }
    &:focus {
        outline-offset: 1px;
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
    }
    &::-moz-focus-inner {
        border: none;
    }
    /* Starter Custom Code - Start */
    font-size: ${({ _sizeVariant }) =>
        get(buttonSizeVariants, [_sizeVariant, "fontSize"]) || buttonSizeVariants.medium.fontSize}px;

    ${({ isLoading }) => {
        if (isLoading) {
            return css`
                > *:not(${LoadingSpinnerStyle}) {
                    visibility: hidden;
                }
            `;
        }
    }}
    /* Starter Custom Code - End */
    ${injectCss}
`;

const omitKeys = ["color", "shape", "size", "sizeVariant"] as const;

type ButtonContextProps = ThemeProps<BaseTheme>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps & ButtonContextProps & HasDisablerContext>( // Starter Custom Code
    (
        props,
        ref, // Starter Custom Code
    ) => {
        const {
            children,
            css: buttonCss,
            disable,
            disabled,
            forwardAs,
            icon,
            typographyProps,
            theme,
            variant = "primary",
            mergeCss,
            isFocusAfterMount,
            /* Starter Custom Code - Start */
            spinnerProps: baseSpinnerProps,
            isLoading,
            href,
            onClick,
            target,
            /* Starter Custom Code - End */
            ...otherProps
        } = props;
        const position = icon?.position;
        const src = icon?.src;
        const buttonRef = useExistingRef(ref); // Starter Custom Code

        let variantProps: ButtonPresentationProps = {};
        if (variant) {
            variantProps = theme.button[variant];
        }
        const { applyProp, applyStyledProp, spreadProps } = applyPropBuilder(props, {
            component: "button",
            propKey: variant,
        });
        const sizeVariant = applyProp("sizeVariant", "medium") as keyof typeof buttonSizeVariants;
        const size = applyProp("size", null) as number | null;
        const resolvedMergeCss = mergeCss ?? variantProps.mergeCss;

        /* Starter Custom Code - Start */
        const spinnerProps = React.useMemo(() => {
            return mergeProps<LoadingSpinnerProps>(
                {
                    size: buttonSizeVariants[sizeVariant].height / 2,
                    css: css`
                        position: absolute;
                    `,
                },
                spreadProps("spinnerProps"),
            );
        }, [baseSpinnerProps, sizeVariant]);

        const as = forwardAs || (props.href ? "a" : undefined);
        const type = !as || as === "button" ? "button" : undefined;
        /* Starter Custom Code - End */

        React.useEffect(() => {
            if (isFocusAfterMount) {
                buttonRef?.current?.focus();
            }
        }, []);

        return (
            <HistoryContext.Consumer /* Starter Custom Code */>
                {({ history }) => /* Starter Custom Code */ {
                    /* Starter Custom Code - Start */
                    const onClickIsFunction = typeof onClick === "function";
                    const forwardOnClick: {
                        onClick?: React.EventHandler<React.MouseEvent>;
                    } = {};
                    const forwardLinkProps: {
                        href?: string;
                        target?: React.HTMLAttributeAnchorTarget;
                        rel?: string;
                    } = {};

                    if (href) {
                        forwardLinkProps.href = href;
                        forwardLinkProps.target = target;
                        if (target && target === "_blank") {
                            forwardLinkProps.rel = "noopener noreferrer";
                        }

                        forwardOnClick.onClick = e => {
                            onClickIsFunction && onClick!(e as any);

                            // cribbed from react-router-dom Link component
                            if (
                                !e.defaultPrevented && // onClick prevented default
                                e.button === 0 && // ignore everything but left clicks
                                (!target || target === "_self") && // let browser handle "target=_blank" etc.
                                !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) // ignore clicks with modifier keys
                            ) {
                                e.preventDefault();
                                if (
                                    history &&
                                    isRelativeUrl(href) &&
                                    !href.startsWith("/RedirectTo/") &&
                                    !href.startsWith("/UserFiles/") &&
                                    !href.match(/^tel:/i)
                                ) {
                                    if (href.indexOf("#") === 0) {
                                        history.push(`${window.location.pathname}${window.location.search}${href}`);
                                    } else {
                                        history.push(href);
                                    }
                                } else {
                                    window.location.href = href;
                                }
                            }
                        };
                    } else if (onClickIsFunction) {
                        forwardOnClick.onClick = onClick;
                    }
                    /* Starter Custom Code - End */

                    return (
                        <ButtonWrapper
                            {...omitMultiple(variantProps, omitKeys)}
                            ref={buttonRef}
                            as={as} // Starter Custom Code
                            css={applyStyledProp("css", resolvedMergeCss)}
                            _color={applyProp("color", "primary")}
                            _shape={applyProp("shape", "rectangle")}
                            _sizeVariant={sizeVariant}
                            _size={size}
                            // Because disabled doesn't accept undefined
                            // eslint-disable-next-line no-unneeded-ternary
                            disabled={disable || disabled ? true : false}
                            /* Starter Custom Code - Start */
                            isLoading={isLoading}
                            hoverTextColor={applyProp("hoverTextColor")}
                            type={type}
                            {...forwardLinkProps}
                            {...forwardOnClick}
                            /* Starter Custom Code - End */
                            {...omitMultiple(otherProps, omitKeys)}
                        >
                            {position === "left" ? (
                                <ButtonIcon
                                    src={src}
                                    size={buttonSizeVariants[sizeVariant].icon}
                                    css={css`
                                        margin-right: ${buttonSizeVariants[sizeVariant].iconPadding}px;
                                    `}
                                />
                            ) : null}
                            {typeof children === "string" ? (
                                <Typography
                                    // Starter Removed - size
                                    mergeCss={mergeCss}
                                    {...spreadProps("typographyProps")}
                                >
                                    {/* Note: if passing an icon into the button, it will only receive button styles
                            if passed as a `ButtonIcon` */}
                                    {children}
                                </Typography>
                            ) : (
                                children
                            )}
                            {position === "right" ? (
                                <ButtonIcon
                                    src={src}
                                    size={buttonSizeVariants[sizeVariant].icon}
                                    css={css`
                                        margin-left: ${buttonSizeVariants[sizeVariant].iconPadding}px;
                                    `}
                                />
                            ) : null}
                            {/* Starter Custom Code - Start */}
                            {isLoading && <LoadingSpinner {...spinnerProps} />}
                            {/* Starter Custom Code - End */}
                        </ButtonWrapper>
                    );
                }}
            </HistoryContext.Consumer>
        );
    },
);

Button.displayName = "Button";
/** @component */
export default withDisabler(withTheme(Button));
export * from "@starter/MobiusOverrides/Button/Props"; // Starter Custom Code
export { ButtonIcon, ButtonWrapper };
